// ==================== REUSABLE CONFIRM DIALOG COMPONENT ====================

import { useEffect, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertTriangle, 
  AlertCircle, 
  CheckCircle, 
  Info, 
  Trash2,
  X,
  Loader2
} from 'lucide-react';
import { cn } from '../../utils/helpers';
import { Button } from './Button';

// ==================== TYPES ====================

export type ConfirmDialogVariant = 'danger' | 'warning' | 'success' | 'info' | 'default';

export interface ConfirmDialogProps {
  /** Controls dialog visibility */
  isOpen: boolean;
  /** Callback when dialog should close */
  onClose: () => void;
  /** Callback when confirmed */
  onConfirm: () => void | Promise<void>;
  /** Dialog title */
  title: string;
  /** Dialog description/message */
  description: ReactNode;
  /** Variant determines icon and color scheme */
  variant?: ConfirmDialogVariant;
  /** Custom icon (overrides variant icon) */
  icon?: ReactNode;
  /** Confirm button text */
  confirmText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Show loading state on confirm button */
  isLoading?: boolean;
  /** Disable closing when clicking backdrop or pressing Escape */
  preventClose?: boolean;
  /** Close dialog when pressing Escape */
  closeOnEscape?: boolean;
}

// ==================== VARIANT CONFIGS ====================

const variantConfig: Record<ConfirmDialogVariant, {
  icon: ReactNode;
  iconBgColor: string;
  iconColor: string;
  confirmButtonVariant: 'danger' | 'primary' | 'success' | 'secondary';
}> = {
  danger: {
    icon: <Trash2 className="w-6 h-6" />,
    iconBgColor: 'bg-red-500/20',
    iconColor: 'text-red-400',
    confirmButtonVariant: 'danger',
  },
  warning: {
    icon: <AlertTriangle className="w-6 h-6" />,
    iconBgColor: 'bg-amber-500/20',
    iconColor: 'text-amber-400',
    confirmButtonVariant: 'primary',
  },
  success: {
    icon: <CheckCircle className="w-6 h-6" />,
    iconBgColor: 'bg-emerald-500/20',
    iconColor: 'text-emerald-400',
    confirmButtonVariant: 'success',
  },
  info: {
    icon: <Info className="w-6 h-6" />,
    iconBgColor: 'bg-blue-500/20',
    iconColor: 'text-blue-400',
    confirmButtonVariant: 'primary',
  },
  default: {
    icon: <AlertCircle className="w-6 h-6" />,
    iconBgColor: 'bg-gray-500/20',
    iconColor: 'text-gray-400',
    confirmButtonVariant: 'primary',
  },
};

// ==================== CONFIRM DIALOG COMPONENT ====================

const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  variant = 'default',
  icon,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  preventClose = false,
  closeOnEscape = true,
}: ConfirmDialogProps) => {
  const config = variantConfig[variant];

  // Handle escape key
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEscape && !preventClose && !isLoading) {
        onClose();
      }
    },
    [closeOnEscape, preventClose, isLoading, onClose]
  );

  // Handle backdrop click
  const handleBackdropClick = useCallback(() => {
    if (!preventClose && !isLoading) {
      onClose();
    }
  }, [preventClose, isLoading, onClose]);

  // Handle confirm - catches errors to prevent unhandled rejections
  // The parent component should handle errors via state
  const handleConfirm = async () => {
    try {
      await onConfirm();
    } catch {
      // Error should be handled by the parent component via state updates
      // We catch here to prevent unhandled promise rejections
    }
  };

  // Add/remove escape listener and body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-60 flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleBackdropClick}
          />

          {/* Dialog Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ 
              type: 'spring', 
              damping: 25, 
              stiffness: 350,
              mass: 0.8
            }}
            className={cn(
              'relative w-full max-w-md overflow-hidden',
              'bg-linear-to-b from-[#1a1f2e] to-[#121723]',
              'border border-gray-800',
              'rounded-2xl shadow-2xl'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              disabled={isLoading}
              className={cn(
                'absolute top-4 right-4 p-1.5 rounded-lg',
                'text-gray-500 hover:text-gray-300 hover:bg-white/5',
                'transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              )}
              aria-label="Close dialog"
            >
              <X size={18} />
            </button>

            {/* Content */}
            <div className="p-6 pt-8 text-center">
              {/* Icon */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ 
                  type: 'spring', 
                  damping: 15, 
                  stiffness: 300,
                  delay: 0.1
                }}
                className={cn(
                  'w-16 h-16 rounded-full mx-auto mb-5',
                  'flex items-center justify-center',
                  config.iconBgColor
                )}
              >
                <span className={config.iconColor}>
                  {icon || config.icon}
                </span>
              </motion.div>

              {/* Title */}
              <motion.h3
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="text-xl font-semibold text-white mb-2"
              >
                {title}
              </motion.h3>

              {/* Description */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-sm text-gray-400 leading-relaxed"
              >
                {description}
              </motion.div>
            </div>

            {/* Actions */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="px-6 pb-6 flex gap-3"
            >
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={isLoading}
                className="flex-1"
              >
                {cancelText}
              </Button>
              <Button
                variant={config.confirmButtonVariant}
                onClick={handleConfirm}
                disabled={isLoading}
                className="flex-1"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  confirmText
                )}
              </Button>
            </motion.div>

            {/* Decorative gradient line */}
            <div 
              className={cn(
                'absolute bottom-0 left-0 right-0 h-1',
                variant === 'danger' && 'bg-linear-to-r from-red-500/50 via-red-400/50 to-red-500/50',
                variant === 'warning' && 'bg-linear-to-r from-amber-500/50 via-amber-400/50 to-amber-500/50',
                variant === 'success' && 'bg-linear-to-r from-emerald-500/50 via-emerald-400/50 to-emerald-500/50',
                variant === 'info' && 'bg-linear-to-r from-blue-500/50 via-blue-400/50 to-blue-500/50',
                variant === 'default' && 'bg-linear-to-r from-gray-500/50 via-gray-400/50 to-gray-500/50'
              )}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

// ==================== HOOK FOR EASY USAGE ====================

export interface UseConfirmDialogOptions {
  title: string;
  description: ReactNode;
  variant?: ConfirmDialogVariant;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void | Promise<void>;
}

// ==================== EXPORTS ====================

export { ConfirmDialog };

